import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Workflow, Calculator } from "lucide-react";
import { SERVICES, STATS, INDUSTRIES } from "../lib/content";

export default function Home() {
const testimonials = [
  {
    quote:
      "Accrue Metrics quickly became an extension of our internal team. Their bookkeeping support and structured workflows helped us maintain consistency during busy periods.",
    role: "Managing Partner",
    company: "CPA Firm",
  },
  {
    quote:
      "What stood out was the reliability and communication. Timelines were respected, processes were organized, and the quality remained consistent.",
    role: "Operations Lead",
    company: "Tax & Advisory Practice",
  },
  {
    quote:
      "Their support reduced operational bottlenecks for our team and improved the efficiency of recurring bookkeeping and reconciliation work.",
    role: "Founder",
    company: "Accounting Practice",
  },
  {
    quote:
      "The workflow felt structured from day one. We appreciated the responsiveness, attention to detail, and dependable execution.",
    role: "Director",
    company: "CPA Services Firm",
  },
  {
    quote:
      "Accrue Metrics helped us streamline several operational tasks that were consuming too much senior staff time.",
    role: "Managing Director",
    company: "Finance & Tax Firm",
  },
];

const [currentTestimonial, setCurrentTestimonial] = useState(0);

useEffect(() => {
  const interval = setInterval(() => {
    setCurrentTestimonial((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  }, 4000);

  return () => clearInterval(interval);
}, []);
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-[#0A1F44] text-white"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1682531369805-344a8c91aa04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc3NjQ2MjcxfDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F44] via-[#0A1F44]/85 to-[#0A1F44]/30" />
        <div className="grain absolute inset-0" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 py-28 lg:grid-cols-12 lg:px-8 lg:py-40">
          <div className="lg:col-span-8">
            <div className="reveal mb-6 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-white/70 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
              Outsourced accounting for CPA firms
            </div>

            <h1
              data-testid="hero-title"
              className="font-serif text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-[5.5rem]"
            >
              The quiet engine
              <br />
              behind your <span className="italic text-[#C9A227]">firm.</span>
            </h1>

            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/75">
              Accrue Metrics gives CPA firms a trained back office: clean books,
              automated workflows, and supervised AI agents — delivered behind the
              scenes so your partners do what only partners can do.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="hover-lift inline-flex items-center gap-3 rounded-sm bg-[#C9A227] px-7 py-4 text-sm font-semibold tracking-wide text-[#0A1F44] hover:bg-[#B38F22]"
              >
                Book a Discovery Call
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="hover-lift inline-flex items-center gap-3 rounded-sm border border-white/30 bg-white/5 px-7 py-4 text-sm font-semibold tracking-wide text-white hover:bg-white/10"
              >
                Explore Services
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 gap-6 border-t border-white/10 pt-10 md:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="font-serif text-3xl font-semibold text-white sm:text-4xl">
                    {s.value}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/55">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:col-span-4 lg:block">
            <div className="relative">
              <div className="absolute -inset-4 -rotate-2 rounded-sm border border-[#C9A227]/40" />
              <div className="relative rounded-sm border border-white/15 bg-white/5 p-8 backdrop-blur-md">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A227]">
                  Engagement preview
                </div>
                <div className="mt-3 font-serif text-2xl leading-tight text-white">
                  "Books closed in 5 business days, every month, with zero partner cleanup."
                </div>
                <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                  <div className="h-9 w-9 rounded-full bg-[#C9A227]/20 ring-1 ring-[#C9A227]/40" />
                  <div>
                    <div className="text-sm font-semibold text-white">Mid-sized CPA firm</div>
                    <div className="text-xs text-white/55">42 staff · Tax & Advisory</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[#E5E7EB] bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 py-8 text-xs uppercase tracking-[0.18em] text-[#475467] lg:flex-row lg:px-8">
          <span className="font-semibold text-[#0A1F44]">Trusted, secure, on-record</span>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3">
            <span>SOC 2 Aligned</span>
            <span className="text-[#E5E7EB]">/</span>
            <span>GDPR Ready</span>
            <span className="text-[#E5E7EB]">/</span>
            <span>NDA Per Engagement</span>
            <span className="text-[#E5E7EB]">/</span>
            <span>QuickBooks · Xero · NetSuite</span>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-[#FAFAFB] py-24 lg:py-32" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
                Five services. One back office.
              </div>
              <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-5xl">
                We don't replace your firm.
                <br />
                We make it bigger.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg leading-relaxed text-[#475467]">
                Every engagement starts with a workflow audit and ends with measurable hours
                returned to your partners. Bookkeeping handles the volume, automation removes
                the friction, and AI drafts the work — under our accountants' review, before
                it ever reaches you.
              </p>
            </div>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((service, index) => (
              <ServiceCard
                  key={service.id}
                  service={service}
                  className={index === 0 ? "md:col-span-2 lg:col-span-2" : ""}
                  featured={index === 0}
    />
  ))}
</div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-24 lg:py-32" data-testid="home-process-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
              How we engage
            </div>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-5xl">
              Four weeks to a working back office.
            </h2>
          </div>

          <div className="mt-16 grid gap-px overflow-hidden rounded-sm border border-[#E5E7EB] bg-[#E5E7EB] md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                n: "01",
                t: "Discovery",
                d: "60-minute scoping call. We map your client portfolio, current workflows, and pain points.",
                icon: Sparkles,
              },
              {
                n: "02",
                t: "Workflow Audit",
                d: "We document your close process step-by-step and identify exactly where hours are being lost.",
                icon: Workflow,
              },
              {
                n: "03",
                t: "Pilot Engagement",
                d: "We onboard 3–5 of your clients in two weeks. You review every output before it leaves our hands.",
                icon: Calculator,
              },
              {
                n: "04",
                t: "Scale & Govern",
                d: "Roll out across your portfolio with monthly QBRs, SLA dashboards, and a named relationship lead.",
                icon: ShieldCheck,
              },
            ].map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.n}
                  data-testid={`process-step-${step.n}`}
                  className="group relative bg-white p-8 transition-colors hover:bg-[#FAFAFB] md:p-10"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-3xl text-[#C9A227]">{step.n}</span>
                    <Icon className="h-5 w-5 text-[#0A1F44] opacity-50 transition-opacity group-hover:opacity-100" />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-semibold text-[#0A1F44]">
                    {step.t}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#475467]">{step.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES PREVIEW */}
      <section className="relative overflow-hidden bg-[#0A1F44] py-24 text-white lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
                Built for the way CPA firms work
              </div>
              <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-white sm:text-5xl">
                Firms we quietly support.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-base leading-relaxed text-white/70">
                From three-person tax shops to 200-staff regional firms, our process flexes
                without compromising the standards your partners signed off on years ago.
              </p>
            </div>
          </div>

          <div className="mt-14 grid gap-px overflow-hidden rounded-sm border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.slice(0, 6).map((ind) => (
              <div
                key={ind.name}
                data-testid={`industry-tile-${ind.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                className="bg-[#0A1F44] p-8 transition-colors hover:bg-[#071630]"
              >
                <h3 className="font-serif text-2xl font-semibold text-white">{ind.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/65">{ind.blurb}</p>
              </div>
            ))}
          </div>

          <div className="mt-14">
            <Link
              to="/industries"
              data-testid="home-industries-cta"
              className="hover-lift inline-flex items-center gap-3 rounded-sm border border-[#C9A227] px-6 py-3 text-sm font-semibold text-[#C9A227] hover:bg-[#C9A227] hover:text-[#0A1F44]"
            >
              See all industries we serve
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

{/* TESTIMONIALS */}
<section className="bg-[#F8F9FC] py-24 lg:py-32">
  <div className="mx-auto max-w-5xl px-6 lg:px-8">

    <div className="text-center">
      <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
        Trusted Support
      </div>

      <h2 className="mt-4 font-serif text-4xl font-medium text-[#0A1F44] sm:text-5xl">
        What Firms Value About Working With Us
      </h2>

      <p className="mx-auto mt-5 max-w-2xl text-[#475467]">
        Structured workflows, reliable execution, and dependable operational support for growing CPA firms.
      </p>
    </div>

    <div className="mt-16">

      <div className="rounded-md border border-[#E5E7EB] bg-white p-10 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl lg:p-14">

        <div className="text-6xl leading-none text-[#C9A227]">
          “
        </div>

        <p className="mt-6 font-serif text-2xl leading-relaxed text-[#0A1F44]">
          {testimonials[currentTestimonial].quote}
        </p>

        <div className="mt-10">
          <div className="text-lg font-semibold text-[#0A1F44]">
            {testimonials[currentTestimonial].role}
          </div>

          <div className="mt-1 text-sm uppercase tracking-[0.18em] text-[#667085]">
            {testimonials[currentTestimonial].company}
          </div>
        </div>
      </div>

      {/* Slider Dots */}
      <div className="mt-8 flex justify-center gap-3">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentTestimonial(index)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              currentTestimonial === index
                ? "w-8 bg-[#C9A227]"
                : "w-2.5 bg-[#D0D5DD]"
            }`}
          />
        ))}
      </div>

    </div>
  </div>
</section>
      {/* CTA BAND */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-sm border border-[#E5E7EB] bg-[#FAFAFB] p-10 lg:p-16">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#C9A227]/10 blur-3xl" />
            <div className="relative grid items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
                  Ready when you are
                </div>
                <h2 className="mt-3 font-serif text-3xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-5xl">
                  Get hours back this quarter.
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#475467]">
                  A 30-minute call. We'll map your close process, identify the three workflows
                  costing your firm the most time, and show you exactly what an Accrue
                  Metrics engagement looks like.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 lg:col-span-4 lg:justify-end">
                <Link
                  to="/contact"
                  data-testid="home-bottom-cta"
                  className="hover-lift inline-flex items-center gap-3 rounded-sm bg-[#0A1F44] px-7 py-4 text-sm font-semibold text-white hover:bg-[#071630]"
                >
                  Schedule Discovery
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ service, className = "", featured = false }) {
  return (
    <Link
      to="/services"
      data-testid={`service-card-${service.id}`}
      className={`card-hover group relative flex flex-col overflow-hidden rounded-sm border border-[#E5E7EB] bg-white ${className}`}
    >
      <div
        className={`relative ${featured ? "h-72 lg:h-80" : "h-48"} overflow-hidden bg-[#0A1F44]`}
      >
        <img
          src={service.image}
          alt={service.title}
          className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/90 via-[#0A1F44]/20 to-transparent" />
        <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A227]">
              Service · 0{SERVICES.findIndex((s) => s.id === service.id) + 1}
            </div>
            <h3 className="mt-1 font-serif text-3xl font-semibold text-white sm:text-4xl">
              {service.title}
            </h3>
          </div>
          <ArrowRight className="h-5 w-5 text-white/80 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-7">
        <p className="font-serif text-xl text-[#0A1F44]">{service.short}</p>
        <p className="mt-3 text-sm leading-relaxed text-[#475467]">
          {service.description}
        </p>
      </div>
    </Link>
  );
}
